"use client";

import React, { useState } from "react";

const DownArrowIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M12.854 9.35375L8.35403 13.8538C8.30759 13.9002 8.25245 13.9371 8.19175 13.9623C8.13105 13.9874 8.06599 14.0004 8.00028 14.0004C7.93457 14.0004 7.86951 13.9874 7.80881 13.9623C7.74811 13.9371 7.69296 13.9002 7.64653 13.8538L3.14653 9.35375C3.05271 9.25993 3 9.13268 3 9C3 8.86732 3.05271 8.74007 3.14653 8.64625C3.24035 8.55243 3.3676 8.49972 3.50028 8.49972C3.63296 8.49972 3.76021 8.55243 3.85403 8.64625L7.50028 12.2931V2.5C7.50028 2.36739 7.55296 2.24021 7.64672 2.14645C7.74049 2.05268 7.86767 2 8.00028 2C8.13289 2 8.26006 2.05268 8.35383 2.14645C8.4476 2.24021 8.50028 2.36739 8.50028 2.5V12.2931L12.1465 8.64625C12.2403 8.55243 12.3676 8.49972 12.5003 8.49972C12.633 8.49972 12.7602 8.55243 12.854 8.64625C12.9478 8.74007 13.0006 8.86732 13.0006 9C13.0006 9.13268 12.9478 9.25993 12.854 9.35375Z"
      fill="#3399FF"
    />
  </svg>
);

/* Answers may be nodes rather than plain strings so one can carry a list; the
   renderer below uses a div instead of a p for that reason — a ul inside a p is
   invalid markup the browser would hoist out, breaking hydration. */
type Faq = { question: string; answer: React.ReactNode };

/* Shared list styling: the answer wrapper is a div, so a ul is valid here. */
const LIST = "mt-2 list-disc space-y-1.5 pl-5";
const SUBLIST = "mt-1.5 list-[circle] space-y-1 pl-5";

const faqs: Faq[] = [
  {
    question: "What is CPA?",
    answer:
      "The CPA, or Certified Public Accountant, is a globally recognized accounting program that certifies individuals in areas like auditing, taxation, financial reporting, and strategic finance. It is particularly valuable for those seeking international accounting roles and is governed by the American Institute of Certified Public Accountants (AICPA).",
  },
  {
    question: "What\u2019s the difference between CFA and CPA?",
    answer: (
      <>
        <p>
          The main difference{" "}
          <a
            href="https://www.mileseducation.com/blog/accounting/cpa-vs-cfa-which-is-better"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#39F] underline"
          >
            between CFA and CPA
          </a>{" "}
          lies in their focus. CFA is geared towards investment analysis,
          portfolio management, and financial markets, making it ideal for
          careers in finance and investment management.
        </p>
        <p className="mt-2">
          On the other hand, CPA focuses on accounting, auditing, taxation, and
          regulatory compliance, preparing professionals for roles in accounting
          firms, corporate finance, and tax advisory. While both offer high
          earning potential, the choice depends on whether you prefer finance
          and investments (CFA) or accounting and taxation (CPA).
        </p>
      </>
    ),
  },
  {
    question: "What are the complete U.S. CPA course fees?",
    answer: (
      <>
        <p>The total course fee is split across three phases:</p>
        <ul className={LIST}>
          <li>
            <span className="font-semibold">
              Phase I (Payable to Miles Education):
            </span>
            <ul className={SUBLIST}>
              <li>Miles CPA Review fees: INR 99,000</li>
            </ul>
          </li>
          <li>
            <span className="font-semibold">
              Phase II (Payable to US agency/state board):
            </span>
            <ul className={SUBLIST}>
              <li>Foreign Academic Credential Evaluation: $400 / INR 34,400</li>
              <li>
                <a
                  href="https://www.mileseducation.com/blog/accounting/cpa-exam-fees-how-much-does-the-cpa-cost"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#39F] underline"
                >
                  CPA Exam Fees
                </a>{" "}
                (First Exam): $900 / INR 77,400
              </li>
            </ul>
          </li>
          <li>
            <span className="font-semibold">
              Phase III (Payable to the state board):
            </span>
            <ul className={SUBLIST}>
              <li>CPA Exam Fees (Remaining 3 Exams): $2,700 / INR 2,32,000</li>
            </ul>
          </li>
        </ul>
      </>
    ),
  },
  {
    question: "What is a CPA course in India, and how does it work?",
    answer: (
      <>
        <p>
          The Certified Public Accountant Course in India refers to the U.S.
          Certified Public Accountant (CPA) program, and is offered by
          institutions like Miles Education.
        </p>
        <p className="mt-2">
          Indian candidates can study and appear for all 4{" "}
          <a href="/cpa/cpa-exam" className="text-[#39F] underline">
            CPA exams
          </a>{" "}
          at their preferred Prometric centers in India. The course duration
          ranges between 12 to 18 months, where candidates are allowed to
          schedule their exams all year-round.
        </p>
        <p className="mt-2">The CPA exam has three core subjects:</p>
        <ul className={LIST}>
          <li>Auditing &amp; Attestation (AUD)</li>
          <li>Financial Accounting &amp; Reporting (FAR)</li>
          <li>Regulation (REG)</li>
        </ul>
        <p className="mt-2">
          Additionally, candidates must choose one discipline to specialize in:
        </p>
        <ul className={LIST}>
          <li>Business Analysis &amp; Reporting (BAR)</li>
          <li>Tax Compliance and Planning (TCP)</li>
          <li>Information Systems &amp; Controls (ISC)</li>
        </ul>
        <p className="mt-2">
          Candidates must pass all three core exams and one discipline of choice
          to earn the CPA qualification.
        </p>
      </>
    ),
  },
  {
    question: "When should I start the CPA course?",
    answer:
      "You should start the CPA course as soon as you meet the basic eligibility, typically a bachelor\u2019s degree with 120\u2013150 credit hours. Starting early, even during your undergraduate studies, can help align your educational and licensing path efficiently.",
  },
  {
    question: "Who should take the CPA?",
    answer:
      "CPA is ideal for accounting, finance, and commerce graduates or professionals who want to work in public accounting firms, multinational corporations, or government agencies. It\u2019s also a great option for CA aspirants, MBAs, and working professionals aiming to specialize in global finance and accounting standards.",
  },
  {
    question: "Where can I find CPA details?",
    answer:
      "You can find comprehensive CPA details on official websites like AICPA, NASBA, or trusted education providers like Miles Education, which guide candidates through eligibility, application, preparation, and licensing. These platforms provide insights into the CPA syllabus, exam structure, state-specific requirements, and career outcomes.",
  },
  {
    question: "Where can I find the full CPA course details?",
    answer:
      "You can find the complete details of the CPA course, including eligibility, exam structure, syllabus, fees, and duration, through official sources like AICPA and trusted training partners like Miles Education. The course includes 4 sections, can be completed in 12\u201318 months, and is recognized in 180+ countries.",
  },
  {
    question: "Where can Indian students access online CPA courses?",
    answer: (
      <>
        <p>
          <a href="/cpa/miles-cpa" className="text-[#39F] underline">
            CPA online courses
          </a>{" "}
          are available and widely preferred by working professionals and
          students in India.
        </p>
        <p className="mt-2">
          Institutions like Miles Education offer structured online training
          with pre-recorded video lectures, live weekend classes, MCQs, mock
          exams, and mentoring, aligned with the AICPA exam blueprint. This
          allows you to prepare for the US CPA exams from anywhere in India,
          with complete flexibility and end-to-end licensing support.
        </p>
      </>
    ),
  },
  {
    question: "How long is the CPA duration?",
    answer: (
      <>
        <p>
          The{" "}
          <a
            href="https://www.mileseducation.com/blog/accounting/cpa-course-duration-fast-track-your-license"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#39F] underline"
          >
            CPA course duration
          </a>{" "}
          varies by candidate but usually takes between 12 to 18 months to
          complete all four exams if pursued consistently. With structured study
          plans and a positive attitude, candidates can certainly complete their
          course within this timeframe.
        </p>
      </>
    ),
  },
];

/* Number of questions shown before the reader taps “See More”. The rest are
   rendered but hidden with a class, so every answer still ships in the HTML
   for crawlers instead of appearing only after a click. */
const VISIBLE_COUNT = 3;

const CpaCourseFaq = () => {
  const [showAll, setShowAll] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpand = (question: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(question)) next.delete(question);
      else next.add(question);
      return next;
    });
  };

  return (
    <section className="w-full bg-white md:py-[50px] max-md:bg-[#F3F3F3] max-md:pb-4">
      <div className="main_container">
        <div className="max-md:rounded-2xl max-md:bg-white max-md:px-4 max-md:py-4">
          <h2 className="text-[24px] md:text-[32px] font-bold leading-[1.3] text-center text-[#333]">
            FAQs
          </h2>

          <div className="mt-5 md:mt-6 md:mx-auto md:max-w-[833px]">
            {faqs?.map((faq, idx) => {
              const isExpanded = expanded.has(faq.question);
              return (
                <div
                  key={faq.question}
                  className={`flex flex-col gap-2.5 rounded-2xl bg-[#F7FBFF] p-4 mb-4 ${
                    !showAll && idx >= VISIBLE_COUNT ? "hidden" : ""
                  }`}
                >
                  <h3 className="text-[14px] md:text-base font-bold leading-normal text-left text-[#333]">
                    {faq.question}
                  </h3>

                  <div
                    className={`relative text-xs md:text-[14px] font-normal leading-normal text-left text-[#999] ${
                      isExpanded ? "" : "max-h-[60px] overflow-hidden"
                    }`}
                  >
                    {faq.answer}
                    {!isExpanded && (
                      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[#F7FBFF] to-transparent" />
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => toggleExpand(faq.question)}
                    aria-expanded={isExpanded}
                    className="inline-flex items-center gap-1 self-start border-0 bg-transparent p-0 text-xs font-bold text-[#39F] md:text-sm"
                  >
                    {isExpanded ? "Read Less" : "Read More"}
                    <span
                      className="inline-flex transition-transform duration-200"
                      style={{
                        transform: isExpanded ? "rotate(180deg)" : "none",
                      }}
                    >
                      <DownArrowIcon />
                    </span>
                  </button>
                </div>
              );
            })}

            {faqs.length > VISIBLE_COUNT && (
              <button
                type="button"
                onClick={() => setShowAll(!showAll)}
                className="mx-auto flex w-full items-center justify-center gap-1 bg-transparent p-0 text-[14px] font-bold text-[#39F]"
              >
                {showAll ? "See Less" : "See More"}
                <span
                  className="inline-flex transition-transform duration-200"
                  style={{ transform: showAll ? "rotate(180deg)" : "none" }}
                >
                  <DownArrowIcon />
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CpaCourseFaq;
